import Styles from './Social.module.scss'
import { Section } from './Section'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import {
  Facebook,
  Instagram,
  LinkAngled,
  LinkedIn,
  TikTokBrand,
  Twitter,
  TwitterBrand,
  YouTubeBrand,
} from 'blaze-ui/icons'
import { BrandPlanTactic } from 'types'
import React from 'react'
import { ExternalIntegrationEnum, ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { IntegrationAccountRow } from './IntegrationAccountRow'
import { DeclarativeToast } from 'common/ToastMessage/DeclarativeToast'
import { ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { FacebookBrand } from 'blaze-ui/icons/35'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

export const Social = ({ tactics, size = 'md' }: { tactics: BrandPlanTactic[]; size?: 'sm' | 'md' }) => {
  const { connect: originalConnect, isConnecting } = useConnectAccountPopup()
  const { isMobile } = useResponsiveDesign()
  const { data, isLoading } = useExternalIntegrations()
  const { trackIntegrationConnected } = useOnboardingTracking()

  const connect = (popup?: Window, options?: any) => {
    const wrappedOptions = {
      ...options,
      onConnect: (integrations: ExternalIntegration[]) => {
        integrations.forEach((integration) => {
          trackIntegrationConnected(integration.provider, true)
        })
        options?.onConnect?.(integrations)
      },
    }
    return originalConnect(popup, wrappedOptions)
  }

  const instagramAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.Instagram,
  )
  const facebookAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.Facebook,
  )
  const linkedinAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.LinkedIn,
  )
  const twitterAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.X,
  )
  const tiktokAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.TikTok,
  )
  const youtubeAccount = data?.external_integrations.find(
    (integration) => integration.provider === ExternalIntegrationEnum.YouTube,
  )

  const showToast = isLoading || isConnecting

  return (
    <>
      {showToast && (
        <DeclarativeToast>
          <ToastMessage toastMessage="Connecting..." variant="loading" />
        </DeclarativeToast>
      )}
      <Section title="Social Media" level={size === 'sm' ? 4 : 2}>
        <div className={Styles.container}>
          <div className={Styles.header}>
            <div />
          </div>

          {[
            tactics.includes(BrandPlanTactic.Instagram) && (
              <>
                {instagramAccount && (
                  <IntegrationAccountRow integration={instagramAccount} icon={Instagram} name="Instagram" />
                )}
                {!instagramAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <InstagramBrand size={24} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>Instagram</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.Instagram} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect Instagram"
                      >
                        {isMobile ? 'Connect' : 'Connect Instagram'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
            tactics.includes(BrandPlanTactic.Facebook) && (
              <>
                {facebookAccount && (
                  <IntegrationAccountRow integration={facebookAccount} icon={Facebook} name="Facebook" />
                )}
                {!facebookAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <FacebookBrand size={24} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>Facebook</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.Facebook} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect Facebook"
                      >
                        {isMobile ? 'Connect' : 'Connect Facebook'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
            tactics.includes(BrandPlanTactic.LinkedIn) && (
              <>
                {linkedinAccount && (
                  <IntegrationAccountRow integration={linkedinAccount} icon={LinkedIn} name="LinkedIn" />
                )}
                {!linkedinAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <LinkedInBrand size={24} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>LinkedIn</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.LinkedIn} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect LinkedIn"
                      >
                        {isMobile ? 'Connect' : 'Connect LinkedIn'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
            tactics.includes(BrandPlanTactic.Twitter) && (
              <>
                {twitterAccount && (
                  <IntegrationAccountRow integration={twitterAccount} icon={Twitter} name="X/Twitter" />
                )}
                {!twitterAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <TwitterBrand size={24} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>X/Twitter</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.X} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect X/Twitter"
                      >
                        {isMobile ? 'Connect' : 'Connect X/Twitter'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
            tactics.includes(BrandPlanTactic.TikTok) && (
              <>
                {tiktokAccount && (
                  <IntegrationAccountRow integration={tiktokAccount} icon={TikTokBrand} name="TikTok" />
                )}
                {!tiktokAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <TikTokBrand size={20} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>TikTok</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.TikTok} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect TikTok"
                      >
                        {isMobile ? 'Connect' : 'Connect TikTok'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
            tactics.includes(BrandPlanTactic.YouTube) && (
              <>
                {youtubeAccount && (
                  <IntegrationAccountRow integration={youtubeAccount} icon={YouTubeBrand} name="YouTube" />
                )}
                {!youtubeAccount && (
                  <div className={Styles.row}>
                    <div className={Styles.rowTitle}>
                      <YouTubeBrand size={24} />
                      <Text variant={isMobile ? 'smallList' : 'largeList'}>YouTube</Text>
                    </div>
                    <InitiateOauthTrigger provider={ExternalIntegrationEnum.YouTube} popUp onInitiatedOauth={connect}>
                      <Button
                        variant="secondary"
                        size={isMobile ? 'sm' : 'lg'}
                        frontIcon={LinkAngled}
                        aria-label="Connect YouTube"
                      >
                        {isMobile ? 'Connect' : 'Connect YouTube'}
                      </Button>
                    </InitiateOauthTrigger>
                    <ResponsiveDesign.Desktop>
                      <div />
                    </ResponsiveDesign.Desktop>
                  </div>
                )}
              </>
            ),
          ]
            .filter(Boolean)
            .map((row, index) => (
              <React.Fragment key={index}>{row}</React.Fragment>
            ))}
        </div>
      </Section>
    </>
  )
}
